import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MessageSquare, Mail, Plus } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Loading, Empty, Pill, Stamp } from "../components/kit";
import { Button } from "../components/ui/button";
import { CallModal, SmsModal, EmailModal } from "../components/actions";
import { titleCase } from "../lib/constants";

const ICON = { call: Phone, sms: MessageSquare, email: Mail };
const COLOR = { call: "text-sky-600 bg-sky-50", sms: "text-emerald-600 bg-emerald-50", email: "text-violet-600 bg-violet-50" };

export default function Communications() {
  const [items, setItems] = useState(null);
  const [type, setType] = useState("");
  const [modal, setModal] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => { const { data } = await api.get(`/communications${type ? `?type=${type}` : ""}`); setItems(data); }, [type]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Communications" subtitle="Every call, text, and email — automatically logged to the contact timeline." testid="comms-header"
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => setModal("call")} data-testid="log-call-btn"><Phone className="w-4 h-4 mr-1.5" />Log Call</Button>
          <Button variant="outline" onClick={() => setModal("sms")} data-testid="send-sms-btn"><MessageSquare className="w-4 h-4 mr-1.5" />SMS</Button>
          <Button className="bg-navy-800 hover:bg-navy-700 text-white" onClick={() => setModal("email")} data-testid="send-email-btn"><Mail className="w-4 h-4 mr-1.5" />Email</Button>
        </div>} />

      <div className="flex gap-2 mb-6">
        {[["", "All"], ["call", "Calls"], ["sms", "SMS"], ["email", "Emails"]].map(([v, l]) => (
          <button key={v} onClick={() => setType(v)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${type === v ? "bg-navy-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`comm-filter-${v || "all"}`}>{l}</button>
        ))}
      </div>

      {!items ? <Loading /> : items.length === 0 ? <Empty title="No communications yet" /> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100" data-testid="comms-list">
          {items.map((m) => {
            const Icon = ICON[m.type] || MessageSquare;
            return (
              <div key={m.id} className="flex gap-3 px-5 py-4 hover:bg-slate-50 cursor-pointer" onClick={() => m.contact_id && nav(`/contacts/${m.contact_id}`)}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${COLOR[m.type]}`}><Icon className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-navy-800">{m.summary || titleCase(m.type)}</p>
                    <span className="text-xs text-slate-400 shrink-0">{new Date(m.timestamp).toLocaleString()}</span>
                  </div>
                  {m.subject && <p className="text-xs font-medium text-slate-600 mt-0.5">{m.subject}</p>}
                  {m.body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{m.body}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    {m.status && <Pill className="text-[10px]">{m.status}</Pill>}
                    {m.outcome && <span className="text-[10px] text-slate-400">{titleCase(m.outcome)}</span>}
                    {m.provider_message && <span className="text-[10px] text-slate-400">· {m.provider_message}</span>}
                    <Stamp prefix="Logged by" by={m.created_by_name || "System"} at={m.timestamp} className="ml-auto" testid={`comm-stamp-${m.id}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CallModal open={modal === "call"} onClose={() => { setModal(null); load(); }} onSaved={load} />
      <SmsModal open={modal === "sms"} onClose={() => { setModal(null); load(); }} onSaved={load} />
      <EmailModal open={modal === "email"} onClose={() => { setModal(null); load(); }} onSaved={load} />
    </div>
  );
}
